import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Reply, Ban, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Privacy: show @username on every note (only fall back to the raw
// `from_user_name` for legacy notes that pre-date the username sweep and
// never had a handle to migrate to). Returns the string ready to render.
const senderLabel = (note) => {
  const uname = note?.from_user_username;
  if (uname) return `@${uname}`;
  return note?.from_user_name || 'someone';
};

const POSTIT_COLORS = [
  '/assets/postit/pink.webp',
  '/assets/postit/yellow.webp',
  '/assets/postit/green.webp',
  '/assets/postit/blue.webp',
];

const REACTIONS = ['👍', '😂', '❤️', '🔥', '🙏', '😮'];

const PostItNote = ({ note, onDismiss, onBlock }) => {
  const [dismissing, setDismissing] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [currentReaction, setCurrentReaction] = useState(note.reaction || null);
  const [thread, setThread] = useState(null); // null = not loaded, [] = empty/just this note
  const menuRef = useRef(null);
  const threadRef = useRef(null);

  const bgImage = useMemo(() => {
    let hash = 0;
    const id = note.note_id || '';
    for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
    return POSTIT_COLORS[Math.abs(hash) % POSTIT_COLORS.length];
  }, [note.note_id]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Identify "me" so we can right-align our own messages in the thread.
  const myUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw).user_id : null;
    } catch { return null; }
  }, []);

  // Fetch the full conversation thread on mount so the recipient can
  // remember what they said before — the previous "reply-only" view
  // dropped the context completely.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/postit/${note.note_id}/thread`, { headers });
        if (cancelled) return;
        // Only treat as a "thread" if there's more than one note in it,
        // otherwise the single-note layout looks nicer.
        setThread(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Silent — fall back to single-note display.
        if (!cancelled) setThread([]);
      }
    })();
    return () => { cancelled = true; };
  }, [note.note_id]);

  // Auto-scroll the thread to the bottom (newest message) on load + on update.
  useEffect(() => {
    if (threadRef.current && thread && thread.length > 1) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [thread]);

  // Close menu on outside click
  useEffect(() => {
    if (!showActions) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showActions]);

  const handleDismiss = async () => {
    setDismissing(true);
    setShowActions(false);
    try {
      await axios.patch(`${API}/postit/${note.note_id}/dismiss`, {}, { headers });
      onDismiss?.(note.note_id);
    } catch {
      toast.error('Could not dismiss note');
      setDismissing(false);
    }
  };

  const handleReact = async (emoji) => {
    try {
      await axios.patch(`${API}/postit/${note.note_id}/react`, { emoji }, { headers });
      setCurrentReaction(emoji);
    } catch {
      toast.error('Could not react');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/postit/${note.note_id}/reply`, { message: replyText.trim() }, { headers });
      // Optimistically append the reply to the thread so the sender sees
      // it land in the conversation immediately, with no extra fetch.
      const newReply = {
        note_id: res?.data?.note_id || `local_${Date.now()}`,
        from_user_id: myUserId,
        from_user_name: 'You',
        from_user_username: null,
        to_user_id: note.from_user_id,
        message: replyText.trim(),
        created_at: new Date().toISOString(),
        parent_note_id: note.note_id,
      };
      setThread((prev) => (Array.isArray(prev) ? [...prev, newReply] : [newReply]));
      toast.success(`Reply sent to ${senderLabel(note)}!`);
      setShowReply(false);
      setReplyText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reply');
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    setShowActions(false);
    try {
      await axios.post(`${API}/postit/block/${note.from_user_id}`, {}, { headers });
      toast.success(`Blocked Post-its from ${senderLabel(note)}`);
      onBlock?.(note.from_user_id);
    } catch {
      toast.error('Could not block user');
    }
  };

  const timeAgo = note.created_at
    ? formatDistanceToNow(new Date(note.created_at), { addSuffix: true })
    : '';

  const hasThread = Array.isArray(thread) && thread.length > 1;

  return (
    <div
      className={`relative transition-all duration-300 ${dismissing ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}
      style={{ width: 320, minHeight: 380 }}
      data-testid={`postit-note-${note.note_id}`}
    >
      {/* Post-it background image */}
      <img
        src={bgImage}
        alt=""
        className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
        draggable={false}
      />

      {/* Content overlay — text sits just under the pin at the top. */}
      <div className={`relative flex flex-col h-full px-12 pb-10 ${hasThread ? 'pt-32' : 'pt-20'}`}>
        {/* Menu button + dropdown */}
        <div className={`absolute right-10 ${hasThread ? 'top-24' : 'top-16'}`} ref={menuRef}>
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1.5 rounded-full hover:bg-black/10 transition-colors bg-black/5"
            data-testid={`postit-menu-${note.note_id}`}
          >
            <span className="text-gray-800 text-base font-bold leading-none">&#8943;</span>
          </button>

          {showActions && (
            <div className="absolute top-8 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[150px]" style={{ zIndex: 9999 }}>
              <button
                onClick={() => { setShowReply(true); setShowActions(false); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid={`postit-reply-btn-${note.note_id}`}
              >
                <Reply className="w-3.5 h-3.5" /> Reply
              </button>
              <button
                onClick={handleDismiss}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid={`postit-delete-btn-${note.note_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Bin
              </button>
              <button
                onClick={handleBlock}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
                data-testid={`postit-block-btn-${note.note_id}`}
              >
                <Ban className="w-3.5 h-3.5" /> Block sender
              </button>
            </div>
          )}
        </div>

        {/* Sender — only shown for single-note view. Threads have per-message labels. */}
        {!hasThread && (
          <p className="text-[11px] font-bold text-gray-800/80 uppercase tracking-wider mb-1.5 text-center">
            {note.is_admin_note ? 'Admin' : senderLabel(note)}
          </p>
        )}

        {/* Message body — thread (scrollable) or single message */}
        {hasThread ? (
          <div
            ref={threadRef}
            className="flex-1 overflow-y-auto pr-1 space-y-2"
            style={{ maxHeight: 180, scrollbarWidth: 'thin' }}
            data-testid={`postit-thread-${note.note_id}`}
          >
            {thread.map((msg) => {
              const isMine = msg.from_user_id === myUserId;
              const label = isMine
                ? 'You'
                : (msg.from_user_username ? `@${msg.from_user_username}` : (msg.from_user_name || 'them'));
              return (
                <div
                  key={msg.note_id}
                  className="flex flex-col items-center text-center"
                  data-testid={`postit-thread-msg-${msg.note_id}`}
                >
                  <span className="text-[9px] uppercase tracking-wider font-bold text-gray-800/60 px-1">
                    {label}
                  </span>
                  <div
                    className="max-w-[92%] px-1 text-gray-900"
                    style={{ fontFamily: "'Caveat', cursive", fontSize: '21px', lineHeight: '1.25' }}
                  >
                    {msg.message}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Single-note view — text sits at the top, just under the pin.
          <p
            className="text-gray-900 leading-snug flex-1 overflow-hidden"
            style={{ fontFamily: "'Caveat', cursive", fontSize: '23px', lineHeight: '1.35' }}
          >
            {note.message}
          </p>
        )}

        {/* Reply input */}
        {showReply && (
          <div className="mt-2 space-y-1.5">
            <Textarea
              placeholder="Write a reply..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              maxLength={300}
              rows={2}
              className="bg-white/60 border-gray-400/30 text-gray-900 text-xs resize-none focus-visible:ring-0 rounded"
              style={{ fontFamily: "'Caveat', cursive", fontSize: '15px' }}
              autoFocus
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={handleReply} disabled={sending || !replyText.trim()} className="h-6 text-[10px] rounded-full px-3">
                {sending ? '...' : 'Send'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowReply(false)} className="h-6 text-[10px] rounded-full px-2">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Bottom row: reactions + timestamp */}
        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex items-center gap-0.5">
            {currentReaction ? (
              <span className="text-lg" title="Your reaction">{currentReaction}</span>
            ) : (
              REACTIONS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleReact(emoji)}
                  className="text-base hover:scale-125 transition-transform"
                  title={`React ${emoji}`}
                >
                  {emoji}
                </button>
              ))
            )}
          </div>
          <p className="text-[8px] text-gray-700/40">{timeAgo}</p>
        </div>
      </div>
    </div>
  );
};

export default PostItNote;
